import React, { useState } from 'react';
import { useDeleteProfile, useProfile, useSaveProfile } from '#@profile/use-profile.js';
import { LoadingIndicator } from '#lib/components/loading.jsx';
import Input from '#lib/components/input.jsx';
import { useForm } from '#lib/hooks/use-form.js';
import useAuth from '#lib/hooks/use-auth.jsx';
import Grid from '#lib/components/grid.jsx';
import Aside from '#lib/components/aside.jsx';
import WarnUserUpdateDialog from '#@profile/components/warn-user-update-dialog.jsx';
import { CheckIcon as SmallCheckIcon } from '@heroicons/react/20/solid';
import DeleteUserDialog from '#@profile/components/delete-user-dialog.jsx';
import { required } from '#lib/hooks/use-validator.js';
import { useMedia } from '#hooks/use-media.js';
import ImagePreview from '#components/images/image-preview.jsx';
import FilePreview from '#components/file-preview.jsx';
import Button from '#lib/components/button.jsx';
import { addClasses, cn } from '#lib/helpers/class.js';
import toast from 'react-hot-toast';
import useAccount from './use-account.js';
import useBlockNavigation from '#hooks/use-block-navigation.js';
import UnsavedChangesModal from '#components/unsaved-changes-modal.jsx';
import { isValid } from '#lib/helpers/errors.js';
import VerifyAuthData from '#src/@profile/components/verify-auth-data.jsx';

function ProfileImage(props) {
    const { imageProfile, handlers } = props;

    return (
        <div className="mb-4 max-w-[300px] flex flex-col items-center">
            <p className="mb-4 md:text-center text-gray-700 text-sm font-bold">{_t.profile.profilePicture()}</p>
            <div className="w-[160px] h-[160px]">
                <FilePreview
                    file={imageProfile}
                    name="imageProfile"
                    coords={imageProfile && imageProfile?.coords}
                    accept="image/png, image/jpeg"
                    width={270}
                    height={270}
                    isSquare={true}
                    rounded={true}
                    {...handlers}
                >
                    {({ previewFile }) => {
                        const initialState = {
                            ...previewFile?.coords,
                            containerWidth: 270
                        };
                        const src = previewFile?.tempSrc ?? previewFile?.src;
                        return <>{previewFile && <ImagePreview imageSrc={src} initialState={initialState} />}</>;
                    }}
                </FilePreview>
            </div>
        </div>
    );
}

function DangerSection({ data }) {
    const [showDeleteDialog, setShowDeleteDialog] = React.useState(false);
    const { signOut } = useAuth();
    const deleteProfileMutation = useDeleteProfile();
    function handleDeleteAccount() {
        deleteProfileMutation.mutate(data?.info.id, {
            onSuccess: () => {
                signOut?.execute({});
            }
        });
    }

    return (
        <>
            <section className="my-7 border-t border-gray-300">
                <h2 className="text-2xl font-bold mt-4 mb-4 text-red-700">{_t.danger.title()}</h2>
                <p className="mb-3">{_t.danger.textCertain()}</p>
                <div className="flex items-center">
                    <Button
                        onClick={() => setShowDeleteDialog(true)}
                        className={addClasses('!text-red-700 !ring-red-700')}
                    >
                        <p className="font-bold">{_t.danger.button()}</p>
                    </Button>
                </div>
            </section>
            <DeleteUserDialog
                isOpen={showDeleteDialog}
                onClose={() => setShowDeleteDialog(false)}
                action={handleDeleteAccount}
                isLoading={deleteProfileMutation.isLoading}
                isError={deleteProfileMutation.isError}
                error={deleteProfileMutation.error}
                confirmationInput={data?.info.email}
            />
        </>
    );
}

export default function Account() {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const profile = useProfile();
    const { proceedNavigation, blocked, reset, setUnsavedChanges } = useBlockNavigation();

    const [errors, setErrors] = useState({});
    const validation = {
        profile: [required]
    };

    const { refreshUserData, user, verifyAttribute, verifyAttributeSubmit, meta } = useAuth();
    const profileInfo = profile.data?.info;
    const save = useSaveProfile({
        onSuccess: () => {
            refreshUserData.execute();
            toast.success(_t.profile.success());
            setUnsavedChanges(() => false);
        },
        onError: (e) => {
            toast.error(e.message);
        }
    });

    const { register, props } = useForm({
        validation,
        initialData: profileInfo,
        onSubmit: (data, _, event) => {
            event.preventDefault();
            if (!emailRegex.test(data.email)) return toast.error(_t.global.validation.invalidEmail());
            if (!isValid({ phoneNumber: data.phoneNumber }, 'phoneNumber', 'phoneNumber')) {
                setErrors({ phoneNumber: { value: true } });
                return toast.error(_t.global.validationError());
            } else {
                setErrors({});
            }
            data.imageProfile = mediaProfileImage?.data?.meta;
            save.mutate(data);
        },
        setUnsavedChanges
    });

    const mediaProfileImage = useMedia({ initialData: profileInfo?.imageProfile, setUnsavedChanges });
    const {
        openVerifyEmail,
        isEmailSent,
        hasEmailVerified,
        confirmWarnUserUpdateDialog,
        updatedFields,
        showWarnUserDialog,
        setShowWarnUserDialog,
        showVerify
    } = useAccount({ save, user, verifyAttribute });

    if (profile.isLoading)
        return (
            <div className={'absolute top-0 right-0 w-full h-full pt-4 flex items-center justify-center'}>
                <LoadingIndicator />
            </div>
        );

    return (
        <React.Fragment>
            <section className="pt-2">
                <h2 className="text-2xl font-bold mb-6 text-neutral-700">{_t.profile.title()}</h2>
                <form {...props.form} className="grid grid-cols-1 md:grid-cols-[minmax(0,_600px)_auto]">
                    <div className="col-span-1 md:col-span-1 md:max-w-[600px]">
                        <div className="mb-4 md:col-span-1">
                            <label htmlFor="firstName" className="block text-gray-700 text-sm font-bold mb-2">
                                {_t.profile.name()}
                            </label>
                            <Input.Full {...register('firstName')} required />
                        </div>
                        <div className="mb-4 md:col-span-1">
                            <label htmlFor="lastName" className="block text-gray-700 text-sm font-bold mb-2">
                                {_t.profile.lastname()}
                            </label>
                            <Input.Full {...register('lastName')} required />
                        </div>
                        <div className="mb-4 md:col-span-1">
                            <label htmlFor="email" className="block text-gray-700 text-sm font-bold mb-2">
                                <div className="flex inline-flex">
                                    {_t.profile.email()}
                                    {hasEmailVerified && (
                                        <span className="ml-3">
                                            <SmallCheckIcon className="h-5 w-6 fill-green-700" />
                                        </span>
                                    )}
                                </div>
                            </label>
                            <Input.Full {...register('email')} type="email" required />
                            {!hasEmailVerified &&
                                (!isEmailSent ? (
                                    <Button
                                        onClick={openVerifyEmail}
                                        disabled={updatedFields.email}
                                        className={addClasses('mt-4 w-32')}
                                    >
                                        {_t.profile.verifyEmail()}
                                    </Button>
                                ) : (
                                    <VerifyAuthData
                                        attribute={showVerify}
                                        verifyAttribute={verifyAttribute}
                                        refreshUserData={refreshUserData.execute}
                                        verifyAttributeSubmit={verifyAttributeSubmit}
                                    />
                                ))}
                        </div>
                        <div className="md:col-span-1">
                            <label htmlFor="phoneNumber" className="block text-gray-700 text-sm font-bold mb-2">
                                {_t.profile.phone()}
                            </label>
                            <Input.Full {...register('phoneNumber')} hasError={errors?.phoneNumber?.value} />
                            <div
                                className={cn(
                                    'text-sm',
                                    errors?.phoneNumber?.value ? 'text-red-500' : 'text-neutral-600'
                                )}
                            >
                                {_t.users.phoneNumberDescription()}
                            </div>
                        </div>
                    </div>
                    <div className="md:px-12 col-span-1 order-first md:order-none w-auto">
                        <ProfileImage
                            imageProfile={mediaProfileImage?.data?.meta}
                            handlers={mediaProfileImage?.handlers}
                        />
                    </div>
                </form>

                <Button
                    look="white"
                    {...props.submit}
                    busy={save.isLoading || mediaProfileImage.isLoading}
                    className={addClasses('w-32 mt-6')}
                >
                    <p className="font-bold">
                        {save.isLoading || mediaProfileImage.isLoading ? _t.global.saveLoad() : _t.global.save()}
                    </p>
                </Button>

                {meta?.data?.mainUser && <DangerSection {...profile} />}
            </section>
            {verifyAttribute.isError && (
                <Aside type="problem" className="mt-6">
                    {verifyAttribute.error}
                </Aside>
            )}
            <WarnUserUpdateDialog
                isOpen={showWarnUserDialog}
                onClose={() => setShowWarnUserDialog(false)}
                isChangingEmail={!!updatedFields?.email}
                action={confirmWarnUserUpdateDialog}
            />
            <UnsavedChangesModal isOpen={blocked} onClose={reset} onConfirm={proceedNavigation} />
        </React.Fragment>
    );
}
